import React, { useState, useEffect, useRef, useMemo } from 'react';
import classNames from 'classnames';
import Options from './Options';
import IconArrowHead from '../IconArrowHead/IconArrowHead';

import css from './AutocompleteSelect.css';

const AutocompleteSelect = ({
  inputValue = { value: '', label: '' },
  onChange,
  onInputChange,
  isLoading = false,
  placeholder,
  inputClassName,
  inputFocusClassName,
  optionsWrapperClassName,
  inputContainerClassName,
  labelClassName,
  errorClassName,
  options = [],
  label = null,
  autocompleteClassName,
  error = null,
  isValidation = true,
  markRequired = false,
  disabled = false,
  onFocus,
  ...rest
}) => {
  const inVal = inputValue?.value || '';
  const inLab = inputValue?.label || '';

  const [phrase, setPhrase] = useState('');
  const [suggestionsOpened, setSuggestionsOpened] = useState(false);
  const [highlightedSuggestionIndex, setHighlightedSuggestionIndex] = useState(-1);

  const refSuggestions = useRef(null);
  const ref = useRef(null);
  const selectedRef = useRef(null);

  // NOWE: Inteligentne filtrowanie (lokalne lub serwerowe)
  const filteredSuggestions = useMemo(() => {
    // Jeśli rodzic przekazał onInputChange, znaczy że sam dostarczy przefiltrowane options z serwera
    if (onInputChange) {
      return options;
    }
    // W przeciwnym razie używamy starego, lokalnego filtrowania
    return options.filter(option => {
      if (!phrase) return true;
      return option.label?.toLowerCase().includes(phrase.toLowerCase());
    });
  }, [options, phrase, onInputChange]);

  useEffect(() => {
    const matchedOption = options.find(o => o.value === inVal);
    const expectedLabel = matchedOption ? matchedOption.label : inLab;

    setPhrase(prevPhrase => {
      if (!suggestionsOpened && prevPhrase !== expectedLabel) {
        return expectedLabel;
      }
      return prevPhrase;
    });
  }, [inVal, inLab, options, suggestionsOpened]);

  useEffect(() => {
    const handleOutsideClick = event => {
      const isOutsideInput = ref.current && !ref.current.contains(event.target);
      const isOutsideSuggestions = refSuggestions.current && !refSuggestions.current.contains(event.target);

      if (isOutsideInput && isOutsideSuggestions) {
        setSuggestionsOpened(false);
        setHighlightedSuggestionIndex(-1);

        setPhrase(prevPhrase => {
          const matchedOption = options.find(o => o.value === inVal);
          const expectedLabel = matchedOption ? matchedOption.label : inLab;
          return expectedLabel !== prevPhrase ? expectedLabel : prevPhrase;
        });
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [inVal, inLab, options]);

  const handleChange = e => {
    const value = e.target.value;
    setPhrase(value);
    setSuggestionsOpened(true);
    setHighlightedSuggestionIndex(-1);

    // NOWE: Przekazujemy wpisaną wartość do rodzica (aby mógł zrobić fetch)
    if (onInputChange) {
      onInputChange(value);
    }

    if (!value && onChange) {
      onChange({ value: '', label: '' });
    }
  };

  const handleClick = (chosenValue, chosenLabel) => {
    if (onChange) {
      onChange({ value: chosenValue, label: chosenLabel });
    }
    setPhrase(chosenLabel);
    finalize();
  };

  const selectHighlighted = index => {
    const targetOption = filteredSuggestions[index];
    if (targetOption) {
      handleClick(targetOption.value, targetOption.label);
    }
  };

  const finalize = () => {
    setSuggestionsOpened(false);
    setHighlightedSuggestionIndex(-1);
    if (ref.current) {
      ref.current.blur();
    }
  };

  const handleKeyDown = event => {
    if (!suggestionsOpened) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        setSuggestionsOpened(true);
        return;
      }
    }

    const suggestionsLength = filteredSuggestions.length;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (suggestionsLength === 0) return;
      setHighlightedSuggestionIndex(prev => prev === -1 ? 0 : (prev + 1) % suggestionsLength);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (suggestionsLength === 0) return;
      setHighlightedSuggestionIndex(prev => prev <= 0 ? suggestionsLength - 1 : prev - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      if (highlightedSuggestionIndex >= 0) {
        selectHighlighted(highlightedSuggestionIndex);
      } else {
        finalize();
      }
    } else if (event.key === 'Escape' || event.key === 'Tab') {
      finalize();
    }
  };

  const handleFocus = e => {
    setSuggestionsOpened(true);
    if (e.target) {
      e.target.select();
    }
    if (onFocus) {
      onFocus();
    }
  };

  // ... (tu pomijam definicje klas classNames, by nie robić śmietnika)
  const optionsWrapperClasses = classNames(optionsWrapperClassName, css.suggestionsWrapper, { [css.validationWrapper]: isValidation });
  const autocompleteClasses = classNames(autocompleteClassName, css.wrapper);
  const inputClasses = classNames(inputClassName, css.input, { [inputFocusClassName]: suggestionsOpened });
  const containerClasses = classNames(css.container, { [css.validation]: isValidation }, inputContainerClassName);
  const labelClasses = classNames(css.labelText, labelClassName);
  const errorClasses = classNames(css.error, errorClassName);

  return (
    <div className={autocompleteClasses}>
      <div className={containerClasses}>
        <label className={css.label}>
          {label && (
            <span className={labelClasses}>
              {label}
              {markRequired && '*'}
            </span>
          )}
          <input
            value={phrase}
            onChange={handleChange}
            onFocus={handleFocus}
            placeholder={placeholder}
            className={inputClasses}
            onKeyDown={handleKeyDown}
            ref={ref}
            disabled={disabled}
            autoComplete="off"
            {...rest}
          />
        </label>
        {error && isValidation && <span className={errorClasses}>{error}</span>}
      </div>
      {suggestionsOpened && !disabled && (
        <div className={optionsWrapperClasses} ref={refSuggestions}>
          {isLoading && (
            <div className={css.loadingOverlay}>
              <span className={css.loadingSpinner}></span>
            </div>
          )
          }
          <Options
            value={phrase}
            handleClick={handleClick}
            highlightedSuggestionIndex={highlightedSuggestionIndex}
            options={filteredSuggestions}
            ref={selectedRef}
          />

        </div>
      )}
    </div>
  );
};

export default AutocompleteSelect;